import { FormatService } from '@/app/common/format/format.service';
import { SearchCreators } from '@/app/pages/manage-brief-tabs-monitor/molecules/mon-creators/atoms/search-creators/search-creators';
import { UploadCsv } from '@/app/pages/manage-brief-tabs-monitor/molecules/mon-creators/atoms/upload-csv/upload-csv';
import { ImageService } from '@/app/services/image/image.service';
import { SocialScreeningService } from '@/app/services/social-screening/social-screening.service';
import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { CreatorCard } from '../../atoms/creator-card/creator-card';
import { ScreeningCreatorRow, ScreeningResultsService } from '../../services/screening-results/screening-results.service';
import { RecruitGroupPicker } from '@/app/molecules/recruit-group-picker/recruit-group-picker';

/** How the creator grid is ordered. */
export type CreatorSort = 'flags' | 'progress' | 'name';

@Component({
  selector: 'scr-screen-creators',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RecruitGroupPicker, SearchCreators, UploadCsv, CreatorCard],
  templateUrl: './screen-creators.html',
  styleUrl: './screen-creators.scss',
  host: { class: 'block h-full min-h-0' },
})
export class ScreenCreators {

  @Input() focussedScreeningBriefId!: string;
  @Output() creatorListSetOut = new EventEmitter<boolean>();

  addCreatorsMode = signal<boolean>(false);
  showAddCreatorPicker = signal<boolean>(false);
  creatorModeTab = signal<1 | 2 | 3 | 4>(2);

  readonly sortOptions: { id: CreatorSort; label: string }[] = [
    { id: 'flags', label: 'Most red flags' },
    { id: 'progress', label: 'Least complete' },
    { id: 'name', label: 'Name' },
  ];

  readonly sort = signal<CreatorSort>('flags');

  readonly creatorRows = computed(() => this.results.creators());

  hasInquiries = computed(() => this.creatorRows().length > 0);

  /** Sorted copy — the service's list stays in load order. */
  readonly sortedCreators = computed<readonly ScreeningCreatorRow[]>(() => {
    const rows = [...this.creatorRows()];
    switch (this.sort()) {
      case 'progress':
        return rows.sort((a, b) => a.progress.completion - b.progress.completion);
      case 'name':
        return rows.sort((a, b) => a.name.localeCompare(b.name));
      default:
        return rows.sort(
          (a, b) =>
            b.progress.flaggedContent - a.progress.flaggedContent ||
            a.progress.completion - b.progress.completion,
        );
    }
  });

  /** Services */
  readonly results = inject(ScreeningResultsService);
  private socialScreeningService = inject(SocialScreeningService);
  public formatService = inject(FormatService);
  public imageService = inject(ImageService);
  /** Services - END */

  addCreatorOptions: { id: 1 | 2 | 3 | 4, name: string, description: string, class: string }[] = [
    { id: 1, name: `Search Creators`, description: `Search for creators by name, handle, or keyword.`, class: 'bg-teal-500/10 text-teal-400' },
    // { id: 2, name: `Add Manually`, description: `Enter creator details one by one.`, class: 'bg-rose-500/10 text-rose-400' },
    { id: 3, name: `Upload CSV`, description: `Upload a CSV using a template.`, class: 'bg-blue-500/10 text-blue-400' },
    // { id: 4, name: `Existing List`, description: `Use an existing list of creators.`, class: 'bg-pink-500/10 text-pink-400' },
  ]

  setSort(sort: CreatorSort): void {
    this.sort.set(sort);
  }

  /** Same selected/idle chrome as the brief rule filters. */
  sortButtonClass(id: CreatorSort): string {
    return id === this.sort()
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  openAddCreatorPicker(): void {
    this.showAddCreatorPicker.set(true);
  }

  closeAddCreatorPicker(): void {
    this.showAddCreatorPicker.set(false);
  }

  selectCreatorOption(option: { id: 1 | 2 | 3 | 4, name: string, description: string, class: string }): void {
    console.log('selectCreatorOption()', option);
    this.showAddCreatorPicker.set(false);
    this.creatorModeTab.set(option.id);
    this.addCreatorsMode.set(true);
  }

  getTabClass(tab: 1 | 2 | 3 | 4) {
    return this.creatorModeTab() === tab
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  listUploaded(uploaded: boolean): void {
    this.addCreatorsMode.set(false);
    this.results.reloadCohort();
  }
}
